import Vue from 'vue';
import { modernAccountRepository } from '@/shells/api/modernAccountRepository';

const ZENDESK_SCRIPT_ID = 'ze-snippet';
const ZENDESK_KEY = 'e469a181-7e42-4a98-ac10-6d3bc1471048';

export function rolesAllowModernChat(roles = {}) {
  return Object.values(roles || {}).some(role => String(role).toLowerCase().includes('chat'));
}

export class ModernChatService {
  constructor(repository = modernAccountRepository, windowRef = window, documentRef = document) {
    this.repository = repository;
    this.window = windowRef;
    this.document = documentRef;
    this.loadPromise = null;
    this.state = Vue.observable({ available: false, status: 'idle', error: null });
  }

  async checkAvailability(roles) {
    if (!rolesAllowModernChat(roles)) {
      this.state.available = false;
      return false;
    }
    try {
      const setting = await this.repository.getChatDisplaySetting();
      this.state.available = setting === 1 || setting === 2;
    } catch (error) {
      this.state.available = false;
    }
    return this.state.available;
  }

  load() {
    if (typeof this.window.zE === 'function') return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.state.status = 'loading';
    this.loadPromise = new Promise((resolve, reject) => {
      const existing = this.document.getElementById(ZENDESK_SCRIPT_ID);
      const script = existing || this.document.createElement('script');
      const timeout = this.window.setTimeout(() => reject(new Error('Chat load timeout')), 12000);
      const finish = () => {
        this.window.clearTimeout(timeout);
        typeof this.window.zE === 'function' ? resolve() : reject(new Error('Chat is unavailable'));
      };
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => {
        this.window.clearTimeout(timeout);
        reject(new Error('Chat could not be loaded'));
      }, { once: true });
      if (!existing) {
        script.id = ZENDESK_SCRIPT_ID;
        script.async = true;
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
        this.document.head.appendChild(script);
      }
    }).then(() => {
      this.state.status = 'ready';
      this.state.error = null;
    }).catch(error => {
      this.state.status = 'error';
      this.state.error = error;
      this.loadPromise = null;
      throw error;
    });

    return this.loadPromise;
  }

  async open() {
    await this.load();
    try {
      this.window.zE('webWidget', 'popout');
    } catch (error) {
      this.window.zE('webWidget', 'show');
      this.window.zE('webWidget', 'open');
    }
  }
}

export const modernChatService = new ModernChatService();
